/**
 * Everyone who said no, and exactly what they said.
 *
 * Read-only, deliberately. Every other list in the app is a place to work
 * from — assign it, log it, move it, edit it — and this one is not. There is
 * no work to do on a row here; its whole job is that the reason is written
 * down where the next person will look, so the buttons that would let
 * somebody start chasing again are simply not drawn.
 *
 * The note is shown in full rather than truncated to a pill. It is the only
 * sentence on the screen that cost anybody anything to write, and it is the
 * one somebody will be reading six weeks from now when the same company
 * arrives again in an import.
 *
 * One component for both boards. A lead that was turned away and a deal that
 * was closed off are the same fact about the same table, and the only thing
 * that differs is which page you happened to be standing on.
 *
 * `kind` is which of the two nos this list is: 'no' for they said so,
 * 'quiet' for nobody ever answered. It changes the icon, the word beside
 * the reason and what an empty list says - not the rows, which the page
 * has already split.
 */
import { useApp } from '../store/AppContext.jsx';
import { leadDetails } from '../domain/crm.js';
import { parseDate } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { Avatar, EmptyState, OwnerPill } from './Primitives.jsx';
import { useWindowed } from './MoreRows.jsx';

/* Newest first: the ones just turned away are the ones anybody is checking. */
const byRecent = (a, b) => (parseDate(b.dropped) || 0) - (parseDate(a.dropped) || 0);

/* What each list is, said in the two places the list says anything. */
const KIND = {
  no: {
    icon: 'x',
    fallback: 'Not interested',
    empty: 'Nobody has been turned away',
    says: 'When somebody says they are not interested, the row comes off the board and lands here with the reason attached.'
  },
  quiet: {
    icon: 'eyeoff',
    fallback: 'No response',
    empty: 'Everybody has answered',
    says: 'When nobody can be reached, the row comes off the board and lands here with whatever was tried written beside it.'
  }
};

export default function DroppedList({ rows: list = [], kind = 'no' }) {
  const k = KIND[kind] || KIND.no;
  const { crm, fmtDate, setSheet } = useApp();
  const sorted = [...list].sort(byRecent);
  const [page, moreRows] = useWindowed(sorted);

  if (!list.length) {
    return (
      <EmptyState icon="check" tone="g-green" title={k.empty}>{k.says}</EmptyState>
    );
  }

  return (
    <div className={`droplist ${kind}`}>
      {page.map(d => {
        const f = leadDetails(crm, d);
        const at = parseDate(d.dropped);
        return (
          <article key={d.id} className="droprow">
            <Avatar name={d.title} />

            <div className="dropwho">
              <b>{d.title}</b>
              <div className="dropmeta">
                {f.email ? <span><Icon name="mail" />{f.email}</span> : null}
                {f.phone ? <span><Icon name="phone" />{f.phone}</span> : null}
                {d.leadType ? <span><Icon name="file" />{d.leadType}</span> : null}
                {d.source ? <span><Icon name="tag" />{d.source}</span> : null}
              </div>
            </div>

            <div className="dropwhy">
              <span className="dropreason">
                <Icon name={k.icon} />{d.dropReason || k.fallback}
              </span>
              {/* In full. See the docblock — this is the sentence the whole
                  tab exists to keep. */}
              {d.dropNote ? <p>{d.dropNote}</p> : null}
            </div>

            <div className="dropwhen">
              {d.owner ? <OwnerPill name={d.owner} /> : <span className="lunowned">Unowned</span>}
              <small>{at ? fmtDate(at) : '—'}</small>
              {/* The one thing worth being able to do from here: read the
                  whole story, including the chase that led to the no. */}
              <button
                type="button"
                className="vw"
                title="Open it — everything the tracker saw, and everything we did"
                onClick={() => setSheet({ kind: 'dealView', id: d.id })}
              >
                <Icon name="eye" />
              </button>
            </div>
          </article>
        );
      })}
      {moreRows}
    </div>
  );
}
